import { Component } from 'react';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';

// Match the JS errors the browser throws when a code-split chunk fails
// to load — typically because the PWA service worker has a stale
// hashed filename in its cache after a deploy. Auto-recovering from
// these is the single biggest stability win in a hot-reload + SW app.
const isChunkLoadError = (err) => {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = (err.message || '').toLowerCase();
  return (
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes('importing a module script failed')
  );
};

// Hard-reset the service worker cache then full-reload. Used when we
// detect a chunk-load failure — the only reliable cure for a stale SW
// is to nuke its caches and let the new index.html land. We guard
// against infinite reload loops with sessionStorage.
async function hardReloadOnce(tag) {
  try {
    const key = `baremc.hardReload.${tag}`;
    if (sessionStorage.getItem(key)) return false; // already tried once this tab
    sessionStorage.setItem(key, String(Date.now()));
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
    }
  } catch (_e) { /* ignore — reload below is the important bit */ }
  window.location.reload();
  return true;
}

// NOTE: Class component cannot use hooks. Strings here are intentionally
// English fallbacks since this UI only renders when a page has crashed.
class PageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorKey: null, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  // Reset the error state whenever the parent tells us we're on a new
  // route. Previously `hasError: true` would persist across navigations
  // because React reuses the same boundary instance when its position
  // in the tree is identical — one crash poisoned every subsequent
  // page until hard refresh. Now the parent passes `resetKey` (the
  // current pathname) and we clear on change.
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.errorKey) {
      return { hasError: false, error: null, errorKey: props.resetKey, recovering: false };
    }
    return null;
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[PageError]', this.props.name || 'Unknown', error, errorInfo);
    if (isChunkLoadError(error)) {
      this.setState({ recovering: true });
      // Async — runs after the spinner UI has had a chance to paint.
      hardReloadOnce('page').catch(() => null);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // ChunkLoadError → show a recovery spinner instead of the
      // "Noget gik galt" screen. The hard reload kicks in
      // automatically; users see a brief loading state, not a scary
      // error.
      if (this.state.recovering) {
        return (
          <div
            className="min-h-[60vh] flex items-center justify-center p-6"
            style={{ background: '#0c1611' }}
            data-testid="page-error-recovering"
          >
            <div className="text-center">
              <RefreshCw className="w-7 h-7 text-emerald-400 animate-spin mx-auto mb-3" />
              <p className="text-zinc-300 text-sm">Opdaterer app…</p>
            </div>
          </div>
        );
      }
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-6" data-testid="page-error-boundary">
          <div className="max-w-sm w-full text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-red-400" />
            </div>
            <h2 className="text-lg font-bold text-white mb-1.5">Noget gik galt</h2>
            <p className="text-zinc-400 text-sm mb-5">Der opstod en fejl. Prøv igen — eller gå tilbage.</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-xl transition-colors"
                data-testid="page-error-retry"
              >
                <RefreshCw className="w-4 h-4" />
                Prøv igen
              </button>
              <button
                onClick={() => window.history.back()}
                className="flex items-center gap-2 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-semibold rounded-xl transition-colors border border-white/10"
                data-testid="page-error-back"
              >
                <ArrowLeft className="w-4 h-4" /> Tilbage
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default PageErrorBoundary;
